/**
 * @param {String} message
 * @param {Object} variant
 * @param {Object} variant.node
 * @param {String} variant.node.title
 */
export const debug = ({ message, variant, data, groupStart, groupEnd }) => {
  if (groupEnd) {
    // eslint-disable-next-line no-console
    return console.groupEnd();
  }

  if (
    window.Benchmade.enableCustomizerDebugging &&
    window.Benchmade.debugVariantTitle.split(',').includes(variant.node.title)
  ) {
    if (groupStart) {
      // eslint-disable-next-line no-console
      console.group(
        `[${variant.node.title}]`,
        ...[message, data].filter((i) => i !== undefined),
      );
    } else {
      // eslint-disable-next-line no-console
      console.log(
        `[${variant.node.title}]`,
        ...[message, data].filter((i) => i !== undefined),
      );
    }
  }

  return null;
};

/* eslint-disable no-bitwise */
/** \
 * @param {File} image
 * @param {String} cartToken
 * @param {String[]} type
 * @returns {Promise<Boolean>}
 */
export const validateImageContent = async ({
  image = File,
  cartToken = '',
  type = [],
}) => {
  const formData = new FormData();
  formData.append('image', image);
  formData.append('cart_token', cartToken);
  formData.append('type', type);

  try {
    const response = await fetch('/apps/service/evaluate/api/image', {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      return response.json();
    }
    return response.status;
  } catch (error) {
    return 413;
  }
};

export const uuidv4 = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16),
  );

export const convertImageToGrayscale = async (
  image = File,
  env = 'preview',
) => {
  const param = env === 'admin-fts' ? 'prod' : 'preview';
  const formData = new FormData();
  formData.append('img', image);
  formData.append('threshold_value', '0.5');
  formData.append('invert', '0');

  /**
   * Gather the binary response into a blob we can use to create a File object
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Streams_API/Using_readable_streams#reading_the_stream
   * */
  const response = await fetch(
    `/apps/service/threekit/api/img/process?env=${param}`,
    {
      method: 'POST',
      body: formData,
    },
  );

  // Check if the fetch was successful
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  const imageUrl = data.fileUrl;

  // Fetch the image data from the image URL
  const imageResponse = await fetch(imageUrl);

  // Check if the image fetch was successful
  if (!imageResponse.ok) {
    throw new Error(`Image fetch error! status: ${imageResponse.status}`);
  }

  // Get the image data as a Blob
  const blob = await imageResponse.blob();

  // Create a File object from the Blob
  const file = new File([blob], image.name, { type: image.type });

  return { file, data };
};

export const getImageAndTextConfigurations = (configuration) => {
  const frontImage = configuration['Add Front Image']?.assetId
    ? 'Add Front Image'
    : '';

  const personalizeFrontImage = configuration['Stock Image']?.assetId
    ? 'Stock Image'
    : '';
  const frontCustomImage = !!(
    configuration['Upload Front Image'] &&
    configuration['Upload Front Image']?.assetId !== ''
  );
  const frontPattern = configuration['Add Front Pattern']?.assetId
    ? 'Add Front Pattern'
    : '';
  const customArt = configuration['Choose Custom Art']?.assetId
    ? 'Choose Custom Art'
    : '';
  const backImage = configuration['Add Back Image']?.assetId
    ? 'Add Back Image'
    : '';
  const backCustomImage = !!(
    configuration['Upload Back Image'] &&
    configuration['Upload Back Image']?.assetId !== ''
  );
  const backPattern = configuration['Add Back Pattern']?.assetId
    ? 'Add Back Pattern'
    : '';
  const frontTextLine1 =
    configuration['Add Front Text'] !== ''
      ? configuration['Add Front Text']
      : '';

  const frontTextLine2 =
    configuration['Add Front Text Line_2'] !== ''
      ? configuration['Add Front Text  Line_2']
      : '';
  const backTextLine1 =
    configuration['Add Back Text'] !== ''
      ? configuration['Add Back Text Line']
      : '';

  const backTextLine2 =
    configuration['Add Back Text'] !== ''
      ? configuration['Add Back Text Line_2']
      : '';

  const personalizeTextOne =
    configuration['Add Text Line 1'] !== ''
      ? configuration['Add Text Line 1']
      : '';

  const personalizeTextTwo =
    configuration['Add Text Line 2'] !== '' &&
    configuration['Add Text Line 2'] !== ' '
      ? configuration['Add Text Line 2']
      : '';

  const personalizeUploadImage = configuration['Upload Image']?.assetId
    ? 'Upload Image'
    : '';

  return {
    frontImage,
    frontCustomImage,
    frontPattern,
    customArt,
    backImage,
    backCustomImage,
    backPattern,
    frontTextLine1,
    frontTextLine2,
    backTextLine1,
    backTextLine2,
    personalizeTextOne,
    personalizeTextTwo,
    personalizeFrontImage,
    personalizeUploadImage,
  };
};

/**
 * Validate text by Azure AI Service
 * @param {String} text
 * @returns {Promise<Boolean>}
 */
export const validateRestrictedWords = async (text) => {
  const data = await fetch('/apps/service/evaluate/api/text', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text: text?.toLowerCase() || '' }),
  })
    .then((response) => response.json())
    .catch((error) => {
      console.error('HTTP error utils.js! /api/evaluate/text :', error);
    });

  if (data?.evaluation?.categoriesAnalysis) {
    return !data.evaluation.categoriesAnalysis.some(
      (item) => item.severity > 0,
    );
  }

  return true;
};
